/**
 * Registers the service worker so a cache header can never strand the app.
 *
 * ## Why this is not the plugin's job
 *
 * `vite-plugin-pwa` generates a `registerSW.js` that calls
 * `navigator.serviceWorker.register(url, { scope })` and offers no way to pass
 * `updateViaCache`. That option is the entire reason this file exists.
 *
 * The default, `updateViaCache: 'imports'`, lets the browser satisfy the
 * *update check* for `sw.js` from the HTTP cache. The production vhost puts a
 * blanket `expires max` on every `.js`, and `sw.js` is one of only two build
 * outputs with no content hash in its name — so it was served live with
 * `Cache-Control: max-age=315360000`, expiring in 2037.
 *
 * A service worker cached that long keeps answering from its own precache
 * forever. The device stops receiving updates entirely and **the server cannot
 * fix it**: only clearing site data on that specific phone will. It cost a
 * morning of chasing a rename that had, in fact, already shipped.
 *
 * `'none'` makes the browser fetch `sw.js` from the network on every update
 * check regardless of what the response headers claim.
 *
 * ## What this does not fix
 *
 * A device that already holds the old registration. That one is stuck until its
 * site data is cleared, which is why the vhost still needs correcting — this
 * only guarantees the situation cannot recur.
 */

/** Checked hourly. A worker who leaves the app open all shift still gets updates. */
const UPDATE_INTERVAL_MS = 60 * 60 * 1000

/**
 * How long `applyUpdate` waits for the new worker to take over before reloading
 * anyway. Generous because it is only ever reached on a path that should not
 * happen; the normal case has already taken over before anybody read the prompt.
 */
const CLAIM_TIMEOUT_MS = 3000

// ---------------------------------------------------------------------------
// "A new build is on the server" — detecting it, and telling the UI
// ---------------------------------------------------------------------------

/*
 * ## Why the registration and not a polled build manifest
 *
 * The obvious alternative is to emit a small `version.json` at build time and
 * fetch it on a timer, comparing it against the constant compiled into this
 * bundle. It was rejected for two reasons, and the first one is this
 * application's own history.
 *
 * **A manifest is a file, and files on this host get cached.** The vhost puts
 * `expires max` on static assets; `sw.js` came back live with `max-age` of ten
 * years, expiring in 2037, and that cost a morning (see above). A `version.json`
 * would be exactly the same shape of file with exactly the same rule applied to
 * it — so the poll would keep reporting the build the device first saw, which
 * is the *precise* failure it was added to detect. Nothing in this repository
 * can stop that happening; CloudPanel owns the vhost and regenerates it.
 *
 * **And it would answer the wrong question.** A manifest says a new build
 * exists on the server. It does not say this device can run it. Reloading on
 * that signal, before the new service worker has precached anything, means a
 * navigation answered out of the *old* precache — a reload that changes
 * nothing, on a prompt that then reappears.
 *
 * The registration answers the question that matters: the new worker has
 * installed *here*, its assets are on this device, and a reload will reach
 * them. It also costs no extra request — `registration.update()` is already
 * called hourly and on every return to the tab, a few lines below.
 *
 * Its weakness, stated plainly: nothing happens on a browser with no service
 * worker, and nothing happens in development, where there is none. Both are
 * acceptable. The people this protects are on installed PWAs, and a developer
 * reloads by reflex.
 *
 * ## Why not `registration.waiting`
 *
 * The usual recipe watches for a worker parked in `waiting` and offers to skip
 * it. That recipe is written for `registerType: 'prompt'`. This app is on
 * `'autoUpdate'` (vite.config.ts), which makes Workbox emit `skipWaiting()` and
 * `clientsClaim()` into the generated worker — so the new worker never dwells
 * in `waiting`, and code that keyed on it would compile, read plausibly, and
 * fire approximately never.
 *
 * `installed` is the honest equivalent: the moment the new build's assets are
 * on the device. `controllerchange` is the moment it takes over this page, and
 * both are watched because the second is what makes a reload land somewhere
 * new — see `applyUpdate`.
 */

let updateReady = false
let controllerTookOver = false

const listeners = new Set<() => void>()

/** The snapshot for `useSyncExternalStore`. A boolean, so identity is stable. */
export function isUpdateReady(): boolean {
  return updateReady
}

export function subscribeToUpdates(listener: () => void): () => void {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

function announceUpdate(): void {
  if (updateReady) return

  updateReady = true
  listeners.forEach((listener) => listener())
}

/**
 * Separated from `registerServiceWorker` so it can be tested.
 *
 * The container is a parameter rather than read from `navigator` for the same
 * reason `lazyRoute` exports its promise: the behaviour worth asserting is a
 * sequence of worker state changes, and reaching it through a global would mean
 * faking half a browser to observe it.
 */
export function watchForUpdate(
  container: ServiceWorkerContainer,
  registration: ServiceWorkerRegistration,
): void {
  /*
   * No controller means this page was not loaded from a service worker — a
   * first visit, or the first load after site data was cleared. The worker
   * installing now is not replacing anything, and there is no stale code in
   * this tab to reload away from. Prompting there would ask somebody to reload
   * the build they are already running.
   */
  if (!container.controller) return

  // A worker that finished installing while this tab was closed, or between the
  // registration call and this line. `updatefound` has already fired for it and
  // will not fire again.
  if (registration.waiting || registration.active !== container.controller) {
    announceUpdate()
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing
    if (!installing) return

    installing.addEventListener('statechange', () => {
      // `activated` as well as `installed`, because skipWaiting can carry the
      // worker through `installed` between two dispatches of this listener.
      if (installing.state === 'installed' || installing.state === 'activated') {
        announceUpdate()
      }
    })
  })

  container.addEventListener('controllerchange', () => {
    controllerTookOver = true
    announceUpdate()
  })
}

/**
 * Reload onto the new build.
 *
 * ## Why this is not just `location.reload()`
 *
 * A reload is a navigation request, and a navigation request is answered by
 * whichever worker controls this page. The old worker's precache holds the old
 * `index.html`, pointing at the old hashed script names — so reloading while it
 * is still in control re-serves the build the user is trying to leave. The
 * prompt then comes back, and the button looks broken in the one way that is
 * hardest to report.
 *
 * `autoUpdate` means the new worker calls `skipWaiting()` and then
 * `clientsClaim()` by itself, usually within a moment of installing and long
 * before anybody has read a banner and tapped it. So the common path here is
 * that `controllerTookOver` is already true and this reloads immediately.
 * Waiting for the event rather than assuming it is what turns "usually" into a
 * guarantee.
 *
 * If it never comes, the registration is removed so the navigation goes to the
 * network, which cannot serve a stale `index.html`. That costs the offline
 * shell for exactly one page load — `registerServiceWorker` runs again on the
 * new page and installs the new worker — so it is gated on actually being
 * online. Offline, this reloads into the same build rather than into nothing;
 * a wasted tap is recoverable and a white screen next to a machine is not.
 *
 * `location.reload()` and not `location.replace()`: reload fires
 * `beforeunload`, which is what lets the unsaved-scores guard in pages/FiveS.tsx
 * put the browser's own "leave this page?" dialog in front of this. That guard
 * is the last thing standing between a tapped prompt and a lost 73-item walk,
 * and it is easy to step around by accident.
 */
export async function applyUpdate(): Promise<void> {
  const container = 'serviceWorker' in navigator ? navigator.serviceWorker : null

  if (container && !controllerTookOver) {
    const tookOver = await whenControllerChanges(container, CLAIM_TIMEOUT_MS)

    if (!tookOver && navigator.onLine) {
      try {
        const registration = await container.getRegistration()
        await registration?.unregister()
      } catch {
        // Reload regardless. The worst case is the reload that would have
        // happened anyway.
      }
    }
  }

  window.location.reload()
}

function whenControllerChanges(
  container: ServiceWorkerContainer,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    const settle = (tookOver: boolean) => {
      container.removeEventListener('controllerchange', onChange)
      clearTimeout(timer)
      resolve(tookOver)
    }

    const onChange = () => settle(true)
    const timer = setTimeout(() => settle(false), timeoutMs)

    container.addEventListener('controllerchange', onChange)
  })
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerServiceWorker(): void {
  // Absent in development, and on any browser without support. The app works
  // without it; it simply has no offline shell.
  if (!('serviceWorker' in navigator)) return

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        watchForUpdate(navigator.serviceWorker, registration)

        /*
         * autoUpdate in the plugin config means the new worker calls
         * skipWaiting itself, so nothing here has to hurry it along — but a tab
         * that stays open for a whole shift never triggers an update check on
         * its own. This asks.
         */
        const timer = window.setInterval(() => {
          void registration.update().catch(() => {})
        }, UPDATE_INTERVAL_MS)

        // Also on returning to the tab, which is what actually happens on a
        // phone: the app is reopened rather than left in the foreground.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            void registration.update().catch(() => {})
          }
        })

        window.addEventListener('beforeunload', () => clearInterval(timer))
      })
      .catch(() => {
        // A failed registration costs the offline shell, nothing else. The
        // queue in lib/offline-queue.ts is IndexedDB and independent of this.
      })
  })
}
